/**
 * DiscountCodeDetector - Service for managing discount codes from URL parameters
 *
 * This service captures discount codes from the ?discount=CODE query parameter
 * and stores them in localStorage for persistence across sessions and pages.
 * It's designed to work with FODZYME's custom discount code system.
 */

import Logger from './logger';
import Event, { type EventEmitter } from './mixins/event';

/** Object.assign below grafts the event mixin onto the prototype at runtime. */
interface DiscountCodeDetector extends EventEmitter {}

class DiscountCodeDetector {
	storageKey: string;
	urlParamName: string;
	discountCode: string | null;
	discountCodeIsNew: boolean;

	constructor() {
		this.storageKey = 'fodzyme_discount_code';
		this.urlParamName = 'discount';
		this.discountCode = null;
		this.discountCodeIsNew = false;

		// Initialize by checking for existing discount code and URL parameters
		this.initialize();
	}

	/**
	 * Initialize the service by checking localStorage and URL parameters
	 */
	initialize() {
		Logger.log(`DiscountCodeDetector initializing...`);

		// Check URL parameters for new discount code
		const newDiscountCode = this.captureDiscountFromURL();
		if (newDiscountCode) {
			this.storeDiscountCode(newDiscountCode);
		} else {
			// Check if we already have a discount code in localStorage
			this.discountCode = this.getStoredDiscountCode();
		}

		Logger.info(`DiscountCodeDetector initialized with discount code: ${this.discountCode || 'none'}`);

		// Listen for window event about going to checkout
		window.addEventListener('checkout:start', () => {
			const checkoutUrl = this.getCheckoutURL();
			if (checkoutUrl) {
				window.location.href = checkoutUrl;
			}
		});
	}

	/**
	 * Capture discount code from URL query parameters
	 * @returns {string|null} The discount code if found, null otherwise
	 */
	captureDiscountFromURL() {
		const urlParams = new URLSearchParams(window.location.search);
		const discountCode = urlParams.get(this.urlParamName);

		if (discountCode && discountCode.trim()) {
			const cleanCode = discountCode.trim();
			this.storeDiscountCode(cleanCode);
			return cleanCode;
		}

		return null;
	}

	/**
	 * Store discount code in localStorage
	 * @param {string} code - The discount code to store
	 */
	storeDiscountCode(code: string) {
		if (code && typeof code === 'string') {
			try {
				localStorage.setItem(this.storageKey, code);
				this.discountCode = code;
				this.discountCodeIsNew = true;
				this.trigger('discount:new', this.discountCode);
				Logger.info(`DiscountCodeDetector stored discount code: ${code}`);
			} catch (error) {
				Logger.error(`Failed to store discount code in localStorage: ${error}`);
			}
		}
	}

	/**
	 * Retrieve discount code from localStorage
	 * @returns {string|null} The stored discount code if found, null otherwise
	 */
	getStoredDiscountCode() {
		try {
			return localStorage.getItem(this.storageKey);
		} catch (error) {
			Logger.error(`Failed to retrieve discount code from localStorage: ${error}`);
			return null;
		}
	}

	/**
	 * Get the current active discount code
	 * @returns {string|null} The current discount code if active, null otherwise
	 */
	getActiveDiscountCode() {
		return this.discountCode;
	}

	/**
	 * Check if there's an active discount code
	 * @returns {boolean} True if a discount code is active, false otherwise
	 */
	hasActiveDiscountCode() {
		return this.discountCode !== null && this.discountCode !== '';
	}

	/**
	 * Check if the discount code is new
	 * @returns {boolean} True if the discount code is new, false otherwise
	 */
	isDiscountCodeNew() {
		return this.discountCodeIsNew;
	}

	/**
	 * Clear the stored discount code
	 */
	clearDiscountCode() {
		try {
			localStorage.removeItem(this.storageKey);
			this.discountCode = null;
			Logger.info(`DiscountCodeDetector cleared discount code`);
		} catch (error) {
			Logger.error(`Failed to clear discount code from localStorage: ${error}`);
		}
	}

	/**
	 * Update discount code (useful for programmatic updates)
	 * @param {string} code - The new discount code
	 */
	updateDiscountCode(code: string | null) {
		if (code === null || code === '') {
			this.clearDiscountCode();
		} else {
			this.storeDiscountCode(code);
		}
	}

	/**
	 * Get the checkout URL with the discount code applied
	 * @returns {string} The checkout URL with discount code
	 */
	getCheckoutURL() {
		if (this.hasActiveDiscountCode()) {
			// Use Shopify's canonical discount endpoint so the code is stored in a
			// session cookie and automatically applied at checkout, even if the user
			// navigates elsewhere before completing their purchase.
			return `/discount/${encodeURIComponent(this.discountCode ?? '')}?redirect=/checkout`;
		}
		return '/checkout';
	}

	/**
	 * Check if the current page has a discount code in the URL
	 * @returns {boolean} True if URL contains discount parameter, false otherwise
	 */
	hasDiscountInURL() {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.has(this.urlParamName);
	}

	/**
	 * Get the discount code from URL without storing it
	 * @returns {string|null} The discount code from URL if present, null otherwise
	 */
	getDiscountFromURL() {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get(this.urlParamName);
	}
}

// Add mixin Event to the class
Object.assign(DiscountCodeDetector.prototype, Event);

export default DiscountCodeDetector;
